import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isRequiredIf from 'react-proptype-conditional-require';
import Tag from '@deliveroo/icon-set/react/Tag';
import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';
import Span from '@deliveroo/consumer-component-library/components/Span';
import P from '@deliveroo/consumer-component-library/components/P';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import StarRating from '../RestaurantStarRating';

import Bubble from '../Bubble';
import withPlaceholder from '../withPlaceholder';
import i18n from '../../utils/i18n';
import styles from './styles.scss';
import greyTile from './images/grey.svg';

export function RestaurantCardHome(props) {
  const {
    categoryNames,
    deliveryFee,
    deliveryStatus,
    deliveryTime,
    imageUrl,
    isEditions,
    isPlaceholder,
    minimumOrderValue,
    name,
    newlyAdded,
    offer,
    placeholderClass,
    priceCategory,
    ratingCount,
    ratingPercent,
    url,
    cardSize,
    lazyLoader,
    isCardStyle,
    onClick,
  } = props;

  const delivery = deliveryFee.presentational || deliveryFee.formatted;
  const mov = minimumOrderValue.presentational || minimumOrderValue.formatted;
  const imageClasses = classnames(styles.image, isPlaceholder && placeholderClass);

  const promoSlot = () => {
    if (newlyAdded && !offer) {
      return (
        <span className={styles.offerText}>
          <Icon component={Tag} color="orange" size={18} spacing="0 x-small 0 0" />
          <P color="orange" size="small">
            {i18n.gettext('New')}
          </P>
        </span>
      );
    }

    if (offer) {
      return (
        <span className={styles.offerText}>
          <Icon component={Tag} color="orange" size={18} spacing="0 x-small 0 0" />
          <P color="orange" size="small">
            {offer}
          </P>
        </span>
      );
    }

    return null;
  };

  const cardDetails = () => (
    <div className={styles.details}>
      <span className={classnames({ [placeholderClass]: isPlaceholder })}>
        <P size="small" isBold spacing="medium 0 0 0">
          {name}
        </P>
      </span>
      {deliveryStatus !== 'UNAVAILABLE' &&
        !isPlaceholder &&
        deliveryTime && (
          <div className={`${styles.bubble} ${styles.homeBubble}`}>
            <Bubble
              time={deliveryTime.text}
              label={deliveryTime.qualifier}
              reverseLabel={deliveryTime.qualifierPosition === 'BEFORE'}
              isPlaceholder={isPlaceholder}
            />
          </div>
        )}
      <div className={styles['tag-list']}>
        {(ratingPercent || priceCategory || !!categoryNames.length) && (
          <ul>
            {!!ratingPercent && (
              <li className={styles.rating}>
                <StarRating
                  value={ratingPercent}
                  count={ratingCount}
                  isPlaceholder={isPlaceholder}
                />
              </li>
            )}
            {priceCategory && (
              <li className={styles['price-category']}>
                <Span size="small" color="grey">
                  {priceCategory}
                </Span>
              </li>
            )}
            {!!categoryNames.length &&
              categoryNames.map((category) => (
                <li key={category}>
                  <Span size="small" color="grey" key={category}>
                    {category}
                    {isPlaceholder && <span className={placeholderClass} />}
                  </Span>
                </li>
              ))}
          </ul>
        )}
        {(delivery || mov) && (
          <ul>
            {delivery && (
              <li>
                <Span size="small" color="grey">
                  {delivery}
                </Span>
              </li>
            )}
            {mov && (
              <li>
                <Span size="small" color="grey">
                  {mov}
                </Span>
              </li>
            )}
          </ul>
        )}
        {promoSlot()}
      </div>
    </div>
  );

  const cardDetailsSmall = () => (
    <div className={styles['details-small']}>
      <span className={classnames({ [placeholderClass]: isPlaceholder })}>
        <P size="small" isBold spacing="small 0 0 0">
          {name}
        </P>
      </span>
      {!!categoryNames.length && (
        <div className={styles.category}>
          <Span size="small" color="grey">
            {categoryNames[0]}
          </Span>
        </div>
      )}
      {deliveryTime &&
        deliveryTime.text && (
          <div>
            <Span size="small" color="grey">
              {deliveryTime.qualifier === 'AFTER'
                ? `${deliveryTime.text} ${deliveryTime.qualifier}`
                : `${deliveryTime.qualifier} ${deliveryTime.text}`}
            </Span>
          </div>
        )}
      {!offer && ratingPercent ? (
        <StarRating
          value={ratingPercent}
          count={ratingCount}
          isPlaceholder={isPlaceholder}
          hideDescriptiveText
        />
      ) : (
        promoSlot()
      )}
    </div>
  );

  return (
    <div
      className={classnames(styles.card, {
        [styles.unavailable]: deliveryStatus === 'UNAVAILABLE',
        [styles['card-medium']]: cardSize === 'MEDIUM',
        [styles['card-small']]: cardSize === 'SMALL',
        [styles['card-style']]: isCardStyle,
      })}
    >
      <a href={url} onClick={onClick}>
        <span className={imageClasses}>
          {!isPlaceholder && (
            <BackgroundImage src={imageUrl || greyTile} lazyLoader={lazyLoader}>
              {deliveryStatus === 'UNAVAILABLE' && (
                <div className={styles.overlay}>
                  <Span color="white" size="small" isBold>
                    {i18n.gettext('restaurant_list.unavailable')}
                  </Span>
                </div>
              )}

              {isEditions && <span className={styles.editions}>Deliveroo Editions</span>}
            </BackgroundImage>
          )}
        </span>
        {cardDetails()}
        {cardSize === 'SMALL' && cardDetailsSmall()}
        {isPlaceholder && (
          <div className={styles['placeholder-tags']}>
            <P>
              <span className={placeholderClass}>Placeholder</span>
            </P>
            <ul>
              <li>
                <span className={placeholderClass}>Placeholder</span>
                <span className={placeholderClass}>Placeholder</span>
                <span className={placeholderClass}>Placeholder</span>
              </li>
            </ul>
          </div>
        )}
      </a>
    </div>
  );
}

RestaurantCardHome.propTypes = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  categoryNames: PropTypes.arrayOf(PropTypes.string),
  offer: PropTypes.string,
  newlyAdded: PropTypes.bool,
  deliveryStatus: PropTypes.string,
  minimumOrderValue: PropTypes.shape({
    formatted: PropTypes.string,
    presentational: PropTypes.string,
  }),
  deliveryTime: PropTypes.shape({
    qualifier: PropTypes.string,
    qualifierPosition: PropTypes.string,
    text: PropTypes.string,
  }),
  deliveryFee: PropTypes.shape({
    formatted: PropTypes.string,
    presentational: PropTypes.string,
  }),
  ratingPercent: PropTypes.number,
  ratingCount: PropTypes.string,
  isEditions: PropTypes.bool,
  priceCategory: PropTypes.string,
  isPlaceholder: PropTypes.bool,
  placeholderClass: PropTypes.string,
  cardSize: PropTypes.string,
  lazyLoader: PropTypes.shape({
    isSupported: PropTypes.bool.isRequired,
    observe: isRequiredIf(PropTypes.func, (props) => props.isSupported),
    unobserve: isRequiredIf(PropTypes.func, (props) => props.isSupported),
  }),
  isCardStyle: PropTypes.bool,
  onClick: PropTypes.func, // Click handler to handle tasks like sending tracking events.
};

RestaurantCardHome.defaultProps = {
  newlyAdded: false,
  deliveryStatus: undefined,
  categoryNames: [],
  offer: '',
  minimumOrderValue: {
    formatted: '',
    presentational: '',
  },
  deliveryFee: {
    formatted: '',
    presentational: '',
  },
  deliveryTime: {
    qualifier: undefined,
    qualifierPosition: undefined,
    text: undefined,
  },
  ratingPercent: null,
  ratingCount: null,
  isPlaceholder: false,
  placeholderClass: null,
  isEditions: false,
  priceCategory: undefined,
  cardSize: 'RESPONSIVE',
  lazyLoader: undefined,
  isCardStyle: false,
  onClick: () => {},
};

export default withPlaceholder(RestaurantCardHome, {
  name: '',
  url: '',
  imageUrl: '',
  minimumOrderValue: {
    formatted: '',
    presentational: '',
  },
  deliveryFee: {
    formatted: '',
    presentational: '',
  },
  categoryNames: [],
  offer: '',
  ratingPercent: null,
  ratingCount: null,
  newlyAdded: false,
});
